import React, { useEffect, useMemo, useRef, useState } from "react";
import { getPresetPickerSubLabel } from "../../utils/passportPresetUtils";
import "./CategorySeeAllModal.css";

export function PresetIcon({ preset }) {
  if (preset?.icon_url) {
    return (
      <img
        src={preset.icon_url}
        alt=""
        className="v1-preset-select-icon"
        decoding="async"
        loading="lazy"
      />
    );
  }
  return (
    <span className="v1-preset-select-icon-fallback" aria-hidden="true">
      📁
    </span>
  );
}

const DEFAULT_COPY = {
  placeholder: "Choose an option",
  searchPlaceholder: "Search",
  loadingText: "Loading…",
  emptySearchText: "No matches for your search",
  emptyListText: "No options available",
};

/**
 * Searchable country/size preset picker — shared by dashboard See-all modals and passport editor.
 * For short lists (e.g. image formats), pass showSearch={false} and overlay="fixed".
 */
export default function PresetSelectDropdown({
  id = "preset-select",
  label,
  presets = [],
  selectedPreset = null,
  onSelect,
  loading = false,
  disabled = false,
  pickerCopy = {},
  categoryId,
  categoryDefault = "",
  className = "",
  showSearch = true,
  showIcons = true,
  /** "absolute" (default) | "fixed" — fixed floats over scroll parents without shifting layout */
  overlay = "absolute",
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const [listOpen, setListOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [fixedPanelStyle, setFixedPanelStyle] = useState(null);
  const copy = { ...DEFAULT_COPY, ...pickerCopy };

  useEffect(() => {
    if (!listOpen) {
      setSearchTerm("");
      setFixedPanelStyle(null);
    }
  }, [listOpen, selectedPreset?.id]);

  useEffect(() => {
    if (!listOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setListOpen(false);
      }
    };

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setListOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    window.addEventListener("mousedown", handlePointerDown);
    return () => {
      window.removeEventListener("keydown", handleEscape);
      window.removeEventListener("mousedown", handlePointerDown);
    };
  }, [listOpen]);

  useEffect(() => {
    if (!listOpen || overlay !== "fixed") return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setFixedPanelStyle({
        position: "fixed",
        top: Math.round(rect.bottom + 8),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        zIndex: 3200,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [listOpen, overlay]);

  const filteredPresets = useMemo(() => {
    if (!showSearch) return presets;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return presets;

    return presets.filter((preset) => {
      const name = (preset.name || "").toLowerCase();
      const subLabel = getPresetPickerSubLabel(
        preset,
        categoryId,
        categoryDefault
      ).toLowerCase();
      return name.includes(term) || subLabel.includes(term);
    });
  }, [presets, searchTerm, categoryId, categoryDefault, showSearch]);

  const handlePick = (preset) => {
    if (typeof onSelect === "function") {
      onSelect(preset);
    }
    setListOpen(false);
  };

  const blockClassName = [
    "v1-category-preset-block",
    className,
    !showSearch ? "v1-preset-select--no-search" : "",
    !showIcons ? "v1-preset-select--no-icons" : "",
    overlay === "fixed" ? "v1-preset-select--fixed-overlay" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={blockClassName} ref={rootRef}>
      {label ? (
        <label className="v1-category-preset-label" htmlFor={id}>
          {label}
        </label>
      ) : null}

      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`v1-preset-select-trigger${listOpen ? " v1-preset-select-trigger--open" : ""}`}
        onClick={() => setListOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={listOpen}
        disabled={disabled || loading}
      >
        <span className="v1-preset-select-trigger-content">
          {selectedPreset ? (
            <>
              {showIcons ? <PresetIcon preset={selectedPreset} /> : null}
              <span className="v1-preset-select-trigger-text">
                <span className="v1-preset-select-trigger-name">{selectedPreset.name}</span>
                <span className="v1-preset-select-trigger-label">
                  {getPresetPickerSubLabel(selectedPreset, categoryId, categoryDefault)}
                </span>
              </span>
            </>
          ) : (
            <span className="v1-preset-select-placeholder">
              {loading ? copy.loadingText : copy.placeholder}
            </span>
          )}
        </span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {listOpen && (
        <div
          className="v1-preset-select-panel"
          role="listbox"
          style={overlay === "fixed" ? fixedPanelStyle || undefined : undefined}
        >
          {showSearch ? (
            <div className="v1-preset-select-search-wrap">
              <input
                type="text"
                className="v1-preset-select-search"
                placeholder={copy.searchPlaceholder}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                autoComplete="off"
                autoFocus
              />
              <span className="v1-preset-select-search-icon" aria-hidden="true">
                🔍
              </span>
            </div>
          ) : null}

          <div className="v1-preset-select-list">
            {loading ? (
              <p className="v1-preset-select-empty">{copy.loadingText}</p>
            ) : filteredPresets.length === 0 ? (
              <p className="v1-preset-select-empty">
                {searchTerm.trim() ? copy.emptySearchText : copy.emptyListText}
              </p>
            ) : (
              filteredPresets.map((preset) => {
                const isSelected = selectedPreset?.id === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`v1-preset-select-option${isSelected ? " v1-preset-select-option--active" : ""}`}
                    onClick={() => handlePick(preset)}
                  >
                    {showIcons ? <PresetIcon preset={preset} /> : null}
                    <span className="v1-preset-select-option-text">
                      <span className="v1-preset-select-option-name">{preset.name}</span>
                      <span className="v1-preset-select-option-label">
                        {getPresetPickerSubLabel(preset, categoryId, categoryDefault)}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
